import { FC, useEffect, useState, useMemo } from 'react'
import { Button, Modal, Checkbox, Radio, RadioChangeEvent } from 'antd'
import style from './style.m.less'

interface ILogoModal {
    visible: boolean
    names: any
    availability: any
    onClose: () => void
    onSubmit: (data: any) => Promise<void> | void
    isNewFormat: boolean
}

export const LogoModal: FC<ILogoModal> = ({
    visible,
    names,
    availability,
    onClose,
    onSubmit,
    isNewFormat,
}) => {
    const [client, setClient] = useState(false)
    const [go, setGo] = useState(false)
    const [owner, setOwner] = useState(false)
    const [pending, setPending] = useState(false)

    const [currentRadioId, setCurrentRadioId] = useState<number>(4)

    const isClientMain = currentRadioId === 0
    const isGoMain = currentRadioId === 1
    const isOwnerMain = currentRadioId === 2
    const isDefaultMain = currentRadioId === 4

    useEffect(() => {
        if (visible) {
            setCurrentRadioId(4)
            setClient(false)
            setGo(false)
            setOwner(false)
            setPending(false)
        }
    }, [visible])

    useEffect(() => {
        setClient(false)
        setGo(false)
        setOwner(false)
    }, [currentRadioId])

    const n = useMemo(
        () => ({
            client_name: names?.client_name ?? 'клиента',
            go_name: names?.go_name ?? 'генератора',
            owner_name: names?.owner_name ?? 'владельца',
            client_type: names?.client_type ?? -1,
            client_id: names?.client_id ?? '',
            generator_id: names?.generator_id ?? '',
            owner_id: names?.owner_id ?? '',
        }),
        [names],
    )

    const clientType = n.client_type

    const clientDisabled =
        (availability ? !availability.client_available : true) || clientType === 0
    const goDisabled = availability ? !availability.go_available : true
    const ownerDisabled = availability ? !availability.owner_available : true

    const allDisabled = !!availability && clientDisabled && goDisabled && ownerDisabled

    const nothingSelected = (isNewFormat ? isDefaultMain : true) && !client && !go && !owner

    const handleRadioChange = (e: RadioChangeEvent) => {
        setCurrentRadioId(e.target.value)
    }

    const handleSubmit = async () => {
        try {
            setPending(true)

            let mainLogoUserId = ''
            if (isNewFormat && !isDefaultMain) {
                if (isClientMain) {
                    mainLogoUserId = n.client_id
                } else if (isGoMain) {
                    mainLogoUserId = n.generator_id
                } else if (isOwnerMain) {
                    mainLogoUserId = n.owner_id
                }
            }

            await onSubmit({
                client_logo: client,
                go_logo: go,
                owner_logo: owner,
                main_logo_id: mainLogoUserId,
            })
        } finally {
            setPending(false)
        }
    }

    const modalWidth = isNewFormat ? 600 : 500

    return (
        <Modal
            title="Логотипы"
            visible={visible}
            onCancel={onClose}
            footer={
                <div
                    className={style?.buttonsWrap}
                    style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}
                >
                    <Button onClick={onClose} className={style?.primaryButtonCancel}>
                        Закрыть
                    </Button>
                    <Button
                        type="primary"
                        className={style?.primaryButton}
                        onClick={handleSubmit}
                        loading={pending}
                        disabled={allDisabled || nothingSelected}
                    >
                        Сформировать
                    </Button>
                </div>
            }
            width={modalWidth}
            style={{
                height: 'max-content',
                borderRadius: 24,
                overflow: 'hidden',
                padding: 0,
            }}
            bodyStyle={{
                width: modalWidth,
                height: 'max-content',
                maxHeight: '800px',
                overflowY: 'auto',
                padding: 20,
            }}
            destroyOnClose
            centered
            closable={false}
            maskClosable={false}
            keyboard={false}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {isNewFormat && (
                    <div>
                        <p style={{ marginBottom: '8px' }}>
                            Шаг 1. Выберите логотип организации для отображения в заголовке
                            свидетельства (для выбора доступен только один логотип)
                        </p>
                        <Radio.Group
                            onChange={handleRadioChange}
                            value={currentRadioId}
                            style={{ width: '100%' }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <div className={style.radioRight}>
                                    <span>без логотипа</span>
                                    <Radio value={4} />
                                </div>

                                <div className={style.radioRight}>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span>{n.client_name}</span>
                                        {!clientDisabled ? (
                                            <span className={style.infoMessage}>
                                                Приобретатель АГ
                                            </span>
                                        ) : (
                                            <span className={style.warningMessage}>
                                                {clientType === 0
                                                    ? 'Логотип не применяется'
                                                    : 'Логотип отсутствует'}
                                            </span>
                                        )}
                                    </div>
                                    <Radio value={0} disabled={clientDisabled} />
                                </div>

                                <div className={style.radioRight}>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span>{n.go_name}</span>
                                        {!goDisabled ? (
                                            <span className={style.infoMessage}>Владелец ГО</span>
                                        ) : (
                                            <span className={style.warningMessage}>
                                                Логотип отсутствует
                                            </span>
                                        )}
                                    </div>
                                    <Radio value={1} disabled={goDisabled} />
                                </div>

                                <div className={style.radioRight}>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span>{n.owner_name}</span>
                                        {!ownerDisabled ? (
                                            <span className={style.infoMessage}>Поставщик АГ</span>
                                        ) : (
                                            <span className={style.warningMessage}>
                                                Логотип отсутствует
                                            </span>
                                        )}
                                    </div>
                                    <Radio value={2} disabled={ownerDisabled} />
                                </div>
                            </div>
                        </Radio.Group>
                        <p style={{ marginTop: '12px', marginBottom: '8px' }}>
                            Шаг 2. Выберите логотипы организаций для отображения внутри
                            свидетельства (доступен множественный выбор)
                        </p>
                    </div>
                )}

                <div className={style.checkboxRightWrap}>
                    <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 370 }}>
                        <span>{n.client_name}</span>
                        {!clientDisabled ? (
                            <span className={style.infoMessage}>Приобретатель АГ</span>
                        ) : (
                            <span className={style.warningMessage}>
                                {clientType === 0
                                    ? 'Логотип не применяется'
                                    : 'Логотип отсутствует'}
                            </span>
                        )}
                    </div>
                    <Checkbox
                        checked={client}
                        disabled={clientDisabled || (isNewFormat && isClientMain)}
                        onChange={(e) => setClient(e.target.checked)}
                    />
                </div>

                <div className={style.checkboxRightWrap}>
                    <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 370 }}>
                        <span>{n.go_name}</span>
                        {!goDisabled ? (
                            <span className={style.infoMessage}>Владелец ГО</span>
                        ) : (
                            <span className={style.warningMessage}>Логотип отсутствует</span>
                        )}
                    </div>
                    <Checkbox
                        checked={go}
                        disabled={goDisabled || (isNewFormat && isGoMain)}
                        onChange={(e) => setGo(e.target.checked)}
                    />
                </div>

                <div className={style.checkboxRightWrap}>
                    <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 370 }}>
                        <span>{n.owner_name}</span>
                        {!ownerDisabled ? (
                            <span className={style.infoMessage}>Поставщик АГ</span>
                        ) : (
                            <span className={style.warningMessage}>Логотип отсутствует</span>
                        )}
                    </div>
                    <Checkbox
                        checked={owner}
                        disabled={ownerDisabled || (isNewFormat && isOwnerMain)}
                        onChange={(e) => setOwner(e.target.checked)}
                    />
                </div>
            </div>
        </Modal>
    )
}
